import { cn } from '@/lib/utils';
import { AlertTriangle, CheckCircle2, Info, Loader2, XCircle } from 'lucide-react';
import type { ReactNode } from 'react';

/**
 * Petites briques d'interface partagées par toutes les pages.
 *
 * Elles sont regroupées ici parce qu'aucune ne justifie son propre fichier :
 * ce sont des conteneurs sans logique, dont le seul rôle est de garantir que
 * deux écrans qui montrent la même chose la montrent de la même façon.
 */

// ---------------------------------------------------------------------------
// Mise en page
// ---------------------------------------------------------------------------

/** Largeur de lecture standard, identique sur toutes les pages. */
export function Container({ children, className }: { children: ReactNode; className?: string }) {
    return <div className={cn('mx-auto w-full max-w-6xl px-5 sm:px-8', className)}>{children}</div>;
}

/** Bloc de contenu avec son rythme vertical. */
export function Section({
    children,
    className,
    id,
}: {
    children: ReactNode;
    className?: string;
    id?: string;
}) {
    return (
        <section id={id} className={cn('py-16 sm:py-20', className)}>
            {children}
        </section>
    );
}

/** Titre de section : sur-titre, titre, sous-titre. */
export function SectionHeading({
    eyebrow,
    title,
    description,
    align = 'left',
}: {
    eyebrow?: string;
    title: string;
    description?: ReactNode;
    align?: 'left' | 'center';
}) {
    return (
        <div className={cn('max-w-2xl', align === 'center' && 'mx-auto text-center')}>
            {eyebrow && (
                <p className="text-brand-600 mb-2 text-xs font-semibold tracking-[0.12em] uppercase">{eyebrow}</p>
            )}
            <h2 className="text-3xl sm:text-4xl">{title}</h2>
            {description && <p className="text-ink-soft mt-4 leading-relaxed">{description}</p>}
        </div>
    );
}

// ---------------------------------------------------------------------------
// Conteneurs
// ---------------------------------------------------------------------------

export function Card({ children, className }: { children: ReactNode; className?: string }) {
    return (
        <div className={cn('border-line bg-surface-raised rounded-xl border shadow-sm', className)}>{children}</div>
    );
}

export function CardHeader({
    title,
    description,
    action,
    icon,
}: {
    title: string;
    description?: ReactNode;
    action?: ReactNode;
    icon?: ReactNode;
}) {
    return (
        <div className="border-line flex items-start justify-between gap-4 border-b px-5 py-4 sm:px-6">
            <div className="flex items-start gap-3">
                {icon && <span className="text-brand-600 mt-0.5">{icon}</span>}
                <div>
                    <h3 className="text-ink text-sm font-semibold">{title}</h3>
                    {description && <p className="text-ink-muted mt-1 text-sm">{description}</p>}
                </div>
            </div>
            {action}
        </div>
    );
}

export function CardBody({ children, className }: { children: ReactNode; className?: string }) {
    return <div className={cn('px-5 py-5 sm:px-6', className)}>{children}</div>;
}

// ---------------------------------------------------------------------------
// Signalétique
// ---------------------------------------------------------------------------

type Tone = 'neutral' | 'brand' | 'positive' | 'caution' | 'critical';

const BADGE_TONES: Record<Tone, string> = {
    neutral: 'bg-surface-sunken text-ink-soft border-line',
    brand: 'bg-brand-50 text-brand-700 border-brand-100',
    positive: 'bg-positive-soft text-positive border-positive/20',
    caution: 'bg-caution-soft text-accent-700 border-caution/25',
    critical: 'bg-critical-soft text-critical border-critical/20',
};

export function Badge({
    children,
    tone = 'neutral',
    className,
}: {
    children: ReactNode;
    tone?: Tone;
    className?: string;
}) {
    return (
        <span
            className={cn(
                'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium',
                BADGE_TONES[tone],
                className,
            )}
        >
            {children}
        </span>
    );
}

const ALERT_TONES: Record<Exclude<Tone, 'brand' | 'neutral'> | 'info', { box: string; icon: ReactNode }> = {
    info: { box: 'bg-brand-50 border-brand-100 text-brand-900', icon: <Info className="size-4" /> },
    positive: {
        box: 'bg-positive-soft border-positive/20 text-positive',
        icon: <CheckCircle2 className="size-4" />,
    },
    caution: {
        box: 'bg-caution-soft border-caution/30 text-accent-700',
        icon: <AlertTriangle className="size-4" />,
    },
    critical: { box: 'bg-critical-soft border-critical/20 text-critical', icon: <XCircle className="size-4" /> },
};

/**
 * Message d'état d'un écran : succès, avertissement, erreur.
 *
 * `role="alert"` sur les tons `critical` et `caution` : le message est
 * immédiatement annoncé, ce qui compte pour une erreur de paiement.
 */
export function Alert({
    tone = 'info',
    title,
    children,
    action,
    className,
}: {
    tone?: keyof typeof ALERT_TONES;
    title?: string;
    children?: ReactNode;
    action?: ReactNode;
    className?: string;
}) {
    const style = ALERT_TONES[tone];

    return (
        <div
            role={tone === 'critical' || tone === 'caution' ? 'alert' : undefined}
            className={cn('flex items-start gap-3 rounded-lg border px-4 py-3 text-sm', style.box, className)}
        >
            <span className="mt-0.5 shrink-0" aria-hidden>
                {style.icon}
            </span>
            <div className="min-w-0 flex-1">
                {title && <p className="font-semibold">{title}</p>}
                {children && <div className={cn('leading-relaxed', title && 'mt-1 opacity-90')}>{children}</div>}
                {action && <div className="mt-3">{action}</div>}
            </div>
        </div>
    );
}

// ---------------------------------------------------------------------------
// États d'attente
// ---------------------------------------------------------------------------

export function Spinner({ className, label }: { className?: string; label?: string }) {
    return (
        <span className="text-ink-muted inline-flex items-center gap-2 text-sm">
            <Loader2 className={cn('size-4 animate-spin', className)} aria-hidden />
            {label && <span>{label}</span>}
        </span>
    );
}

/** Bloc gris pulsant qui tient la place d'un contenu en cours de chargement. */
export function Skeleton({ className }: { className?: string }) {
    return <div className={cn('bg-line/70 animate-pulse rounded-md', className)} aria-hidden />;
}

/** Message d'absence de résultat, homogène sur tout le site. */
export function EmptyState({
    icon,
    title,
    description,
    action,
}: {
    icon?: ReactNode;
    title: string;
    description?: ReactNode;
    action?: ReactNode;
}) {
    return (
        <div className="border-line bg-surface-sunken rounded-xl border border-dashed px-6 py-12 text-center">
            {icon && <div className="text-ink-muted mb-3 flex justify-center">{icon}</div>}
            <p className="text-ink font-medium">{title}</p>
            {description && <p className="text-ink-muted mx-auto mt-2 max-w-md text-sm">{description}</p>}
            {action && <div className="mt-5">{action}</div>}
        </div>
    );
}

// ---------------------------------------------------------------------------
// Présentation de données
// ---------------------------------------------------------------------------

/** Ligne « libellé / valeur » d'une fiche. */
export function DataRow({
    label,
    value,
    className,
}: {
    label: string;
    value: ReactNode;
    className?: string;
}) {
    return (
        <div className={cn('border-line/70 flex gap-4 border-b py-2.5 last:border-b-0', className)}>
            <dt className="text-ink-muted w-2/5 shrink-0 text-sm">{label}</dt>
            <dd className="text-ink min-w-0 flex-1 text-sm font-medium break-words">{value}</dd>
        </div>
    );
}

export function DataList({ children, className }: { children: ReactNode; className?: string }) {
    return <dl className={cn('', className)}>{children}</dl>;
}
